import React, { useEffect, useState, useMemo } from "react";

import { useExecutionEngagementDetails } from "@/contexts/ExecutionEngagementDetailsContext";
import { applyLimitedReviewExecutionCardFilter } from "@/utils/execution-limited-review-ui";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { ShieldCheck, Layers, Lock, FileCheck2, XCircle } from "lucide-react";
import DocumentListWithActions from "../../documents/DocumentListWithActions";
import { InformationIPEIUCSection } from "../../ui/information-ipe-iuc-section";
import { useWorkspaceDeficiencies } from "../../../hooks/useWorkspaceDeficiencies";
import { useWorkspaceMisstatements } from "../../../hooks/useWorkspaceMisstatements";
import { getFileSectionList } from "../../../utils/workspace-section-mapping";

type OtherFinancialAssetsLandingProps = {
  onSelect: (section: string) => void;
};

const baseCategories: Array<{
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
}> = [
  { id: "ofa-tailoring", title: "Tailoring Questions", description: "Customize the audit program for Other Financial Assets.", icon: Layers, accent: "from-blue-500/20 to-indigo-500/20" },
  { id: "ofa-romms", title: "RoMMs", description: "Identify and assess risks for Other Financial Assets.", icon: ShieldCheck, accent: "from-amber-500/20 to-orange-500/20" },
  { id: "ofa-controls", title: "Internal Controls", description: "Evaluate design and implementation of controls.", icon: Lock, accent: "from-emerald-500/20 to-teal-500/20" },
  { id: "ofa-substantive", title: "Substantive Procedures", description: "Perform detailed procedures over Other Financial Assets.", icon: FileCheck2, accent: "from-fuchsia-500/20 to-purple-500/20" },
];

const getCategories = (hasDeficiencies: boolean) => {
  const categories = [...baseCategories];
  if (hasDeficiencies) {
    categories.push({
      id: "ofa-deficiencies",
      title: "Deficiencies",
      description: "View deficiencies in internal controls linked to this workspace.",
      icon: XCircle,
      accent: "from-red-500/20 to-rose-500/20",
    });
  }
  return categories;
};

export default function OtherFinancialAssetsLanding({ onSelect }: OtherFinancialAssetsLandingProps) {
  const workspaceName = "Other Financial Assets";
  const { deficiencies: workspaceDeficiencies, loading: defLoading, hasDeficiencies } = useWorkspaceDeficiencies(workspaceName);
  const {
    misstatements: workspaceMisstatements,
    loading: misLoading,
    hasMisstatements,
  } = useWorkspaceMisstatements(workspaceName);
  const [docs, setDocs] = useState<Array<{ name: string; url: string; prep_so: any; rev_so: any; conc_so: any; eqr_so: any; partner_so: any }>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [sharePointFiles, setSharePointFiles] = useState<Array<{ name: string }>>([]);
  const [isLoadingSharePointFiles, setIsLoadingSharePointFiles] = useState<boolean>(false);
  const [existingFiles, setExistingFiles] = useState<Record<string, string>>({});

  const loadDocs = async () => {
    try {
      setLoading(true);
      setError("");
      const sectionKey = "Execution_OFA";
      const res = await (window as any)?.sharePointAPI?.loadDocumentListBySection?.(sectionKey);
      if (res?.success) {
        setDocs(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res?.error || "Failed to load document list");
      }
    } catch (e: any) {
      setError(e?.message || "Unexpected error while loading document list");
    } finally {
      setLoading(false);
    }
  };

  const loadSharePointFiles = async () => {
    try {
      setIsLoadingSharePointFiles(true);
      if ((window as any).sharePointAPI?.findFilesBySection) {
        const result = await (window as any).sharePointAPI.findFilesBySection("", "client");
        if (result.success && result.data?.files) {
          setSharePointFiles(result.data.files.map((name: string) => ({ name })));
        } else {
          setSharePointFiles([]);
        }
      } else {
        setSharePointFiles([]);
      }
    } catch {
      setSharePointFiles([]);
    } finally {
      setIsLoadingSharePointFiles(false);
    }
  };

  const loadDbJson = async () => {
    try {
      const existingFilesMap: Record<string, string> = {};
      if ((window as any).sharePointAPI?.downloadLibrariesInformation) {
        const result = await (window as any).sharePointAPI.downloadLibrariesInformation();
        if (result?.success && result?.data?.information) {
          const { WORKSPACE_DOCUMENT_CARDS } = await import("../../../utils/workspace-document-cards");
          const documentCards = WORKSPACE_DOCUMENT_CARDS[workspaceName] || [];
          const infoNameToSectionCode: Record<string, string> = {};
          for (const card of documentCards) {
            infoNameToSectionCode[card.informationName] = card.sectionCode;
          }
          const matchingEntries = result.data.information.filter((entry: any) => entry.workspace && entry.workspace.includes(workspaceName));
          for (const entry of matchingEntries) {
            const sectionCode = infoNameToSectionCode[entry.information_name];
            if (sectionCode && entry.file_name) {
              existingFilesMap[sectionCode] = entry.file_name;
            }
          }
        }
      }
      setExistingFiles(existingFilesMap);
    } catch {
      setExistingFiles({});
    }
  };

  useEffect(() => {
    const run = async () => {
      await loadDocs();
      await loadSharePointFiles();
      await loadDbJson();
    };
    run();
  }, []);

  const { isLimitedReviewExecution } = useExecutionEngagementDetails();
  const displayCategories = useMemo(
    () =>
      applyLimitedReviewExecutionCardFilter(
        getCategories(hasDeficiencies),
        isLimitedReviewExecution,
      ),
    [hasDeficiencies, isLimitedReviewExecution],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Other Financial Assets</h2>
          <p className="text-sm text-white/60">Choose a workflow to proceed. You can return here anytime.</p>
        </div>
        <Badge className="bg-white/10 text-white/80">Execution</Badge>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {displayCategories.map((cat) => {
          const Icon = cat.icon;
          const isDeficiencyCard = cat.id === "ofa-deficiencies";
          const isMisstatementCard = cat.id.endsWith("-misstatements");
          return (
            <Card
              key={cat.id}
              className="group cursor-pointer border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10"
              onClick={() => onSelect(cat.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className={`mb-3 inline-flex rounded-lg bg-gradient-to-br ${cat.accent} p-2`}>
                      <Icon className="h-5 w-5 text-white/80" />
                    </div>
                    <div className="mb-1 text-sm font-semibold">{cat.title}</div>
                    <div className="mb-3 text-xs text-white/60">{cat.description}</div>
                  </div>
                  {(isDeficiencyCard || isMisstatementCard) && (
                    <Badge variant="outline" className="ml-auto text-[10px] uppercase tracking-wide">
                      {isDeficiencyCard
                        ? defLoading
                          ? "Loading"
                          : `${workspaceDeficiencies.length} item(s)`
                        : misLoading
                          ? "Loading"
                          : `${workspaceMisstatements.length} item(s)`}
                    </Badge>
                  )}
                </div>
                <Button size="sm" variant="outline" className="border-white/10 text-xs text-white/80 group-hover:bg-white/10 group-hover:text-white">
                  Continue
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <InformationIPEIUCSection
        workspaceName={workspaceName}
        workspaceId="OFA"
        area="Execution"
        onFilesUploaded={async () => {
          await loadDocs();
          await loadDbJson();
        }}
        onRemoveFile={async () => {
          await loadDbJson();
        }}
        sharePointFiles={sharePointFiles}
        isLoadingSharePointFiles={isLoadingSharePointFiles}
        existingFiles={existingFiles}
      />

      <DocumentListWithActions
        documents={docs}
        loading={loading}
        error={error}
        onRefresh={loadDocs}
        title="Document List"
        showUploadButton={true}
        uploadSectionList={getFileSectionList(workspaceName)}
        uploadFolderName="client"
        uploadMultiple={true}
        workspaceName={workspaceName}
        onNavigateToConfirmationLibrary={(w) => onSelect?.("confirmation-library:" + encodeURIComponent(w))}
        onNavigateToSamplingLibrary={(w) => onSelect?.("sampling-library:" + encodeURIComponent(w))}
      />
    </div>
  );
}

